/**
 * View Model Builder
 *
 * Assembles a MonthlyReportViewModel from aggregated reports.
 * This is the final transformation step before rendering — the view model
 * contains all pre-computed data the renderer needs without any business logic.
 *
 * Responsibilities:
 * - Build header (patient name, month label, generation timestamp)
 * - Compute executive summary counts from aggregated data
 * - Build all output chart data series via chartDataBuilders
 * - Assemble meal matrix from input report daily rows
 * - Assemble feeding summary from input report
 * - Build notes timeline from notes report (sorted chronologically)
 * - Pass through raw events for audit appendix
 * - Guarantee all collection fields are defined arrays (never null/undefined)
 */

import moment from 'moment';

import type {
  MonthlyInputReport,
  MonthlyOutputReport,
  MonthlyNotesReport,
  MonthlyReportViewModel,
  NormalizedOutputRecord,
  RawEventEntry,
  TaggedNote,
  DailyInputRow,
} from '../types';

import {
  buildDailyStoolCountChart,
  buildDailyUrineCountChart,
  buildStoolShapeScatter,
  buildStoolSizeScatter,
  buildStoolColorStacked,
  buildUrineColorLine,
  buildUrineTypeHistogram,
  buildUrineColorStacked,
} from '../aggregation/chartDataBuilders';
import { buildBloodPressureClusterGraphs, buildGlucoseClusterGraphs, buildGroupedVitalSignsRecord } from '../aggregation/clusterGraphBuilders';
import type { BpReading, ClusterResult, GlucoseReading } from '../clustering/types';

export interface BuildMonthlyReportViewModelParams {
  patientName: string;
  patientId: string;
  year: number;
  month: number;
  inputReport: MonthlyInputReport;
  outputReport: MonthlyOutputReport;
  notesReport: MonthlyNotesReport;
  vitalCounts: {
    heartrate: number;
    bloodpressure: number;
    temperature: number;
    spo2: number;
  };
  stoolRecords: NormalizedOutputRecord[];
  urineRecords: NormalizedOutputRecord[];
  rawEvents: RawEventEntry[];
  bloodPressureClusters?: ClusterResult<BpReading>;
  glucoseClusters?: ClusterResult<GlucoseReading>;
  glucoseDataPointCount?: number;
}

/**
 * Assemble a complete MonthlyReportViewModel from aggregated reports and metadata.
 *
 * All collection fields in the returned view model are guaranteed to be defined arrays,
 * even when the source data is empty.
 */
export function buildMonthlyReportViewModel(
  params: BuildMonthlyReportViewModelParams
): MonthlyReportViewModel {
  const {
    patientName,
    patientId,
    year,
    month,
    inputReport,
    outputReport,
    notesReport,
    vitalCounts,
    stoolRecords,
    urineRecords,
    rawEvents,
    bloodPressureClusters = { clusters: [], unscheduled: [], largestClusterSize: 0 },
    glucoseClusters = { clusters: [], unscheduled: [], largestClusterSize: 0 },
    glucoseDataPointCount = 0,
  } = params;

  // ─── Header ──────────────────────────────────────────────────────────────────
  const reportMonth = moment({ year, month: month - 1 }).format('MMMM YYYY');
  const generatedAt = moment().toISOString();

  const header = {
    patientName,
    patientId,
    reportMonth,
    generatedAt,
  };

  // ─── Executive Summary ───────────────────────────────────────────────────────
  const vitalRecordCount =
    vitalCounts.heartrate +
    vitalCounts.bloodpressure +
    vitalCounts.temperature +
    vitalCounts.spo2;

  const feedingRecordCount = inputReport.dailyRows.reduce(
    (sum, row) =>
      sum +
      row.meals.breakfast.length +
      row.meals.lunch.length +
      row.meals.teaTime.length +
      row.meals.dinner.length +
      row.meals.fruits.length +
      row.meals.snack.length,
    0
  );

  const outputRecordCount = outputReport.totalOutputCount;
  const stoolCount = outputReport.totalStoolCount;
  const urineCount = outputReport.totalUrineCount;
  const totalNotesCount = notesReport.totalNotes;
  const clinicalAttentionCount = notesReport.attentionNotes.filter(
    (tn) => tn.severityTag === 'Clinical Attention'
  ).length;

  const executiveSummary = {
    vitalRecordCount,
    feedingRecordCount,
    outputRecordCount,
    stoolCount,
    urineCount,
    totalNotesCount,
    clinicalAttentionCount,
    mealBreakdown: (inputReport.dailyRows || []).reduce(
      (totals, day) => ({
        breakfast: totals.breakfast + day.meals.breakfast.length,
        lunch: totals.lunch + day.meals.lunch.length,
        teaTime: totals.teaTime + day.meals.teaTime.length,
        dinner: totals.dinner + day.meals.dinner.length,
        fruits: totals.fruits + day.meals.fruits.length,
        snack: totals.snack + day.meals.snack.length,
      }),
      { breakfast: 0, lunch: 0, teaTime: 0, dinner: 0, fruits: 0, snack: 0 }
    ),
    vitalBreakdown: {
      bloodPressure: vitalCounts.bloodpressure,
      heartRate: vitalCounts.heartrate,
      temperature: vitalCounts.temperature,
      oxygen: vitalCounts.spo2,
    },
  };

  // ─── Output Charts ───────────────────────────────────────────────────────────
  const dailyAggregates = outputReport.dailyAggregates || [];

  const outputCharts: MonthlyReportViewModel['outputCharts'] = {
    dailyStoolCount: buildDailyStoolCountChart(dailyAggregates, year, month),
    dailyUrineCount: buildDailyUrineCountChart(dailyAggregates, year, month),
    stoolShape: buildStoolShapeScatter(stoolRecords || []),
    stoolSize: buildStoolSizeScatter(stoolRecords || []),
    stoolColorStacked: buildStoolColorStacked(dailyAggregates, stoolRecords || [], year, month),
    urineColorLine: buildUrineColorLine(dailyAggregates, year, month),
    urineTypeHistogram: buildUrineTypeHistogram(urineRecords || []),
    urineColorStacked: buildUrineColorStacked(dailyAggregates, urineRecords || [], year, month),
  };

  // ─── Meal Matrix ─────────────────────────────────────────────────────────────
  const mealMatrix: DailyInputRow[] = inputReport.dailyRows || [];

  // ─── Feeding Summary ─────────────────────────────────────────────────────────
  const feedingSummary = {
    normalCount: inputReport.feedingTypeSummary.normalCount,
    ngtCount: inputReport.feedingTypeSummary.ngtCount,
    pegTubeCount: inputReport.feedingTypeSummary.pegTubeCount,
  };

  // ─── Notes Timeline ──────────────────────────────────────────────────────────
  // Flatten all notes from notesByDate into a single sorted array
  const notesTimeline: TaggedNote[] = buildNotesTimeline(notesReport);

  // ─── Raw Events ──────────────────────────────────────────────────────────────
  const safeRawEvents: RawEventEntry[] = rawEvents || [];
  const bpGraphs = buildBloodPressureClusterGraphs(bloodPressureClusters);
  const glucose = buildGlucoseClusterGraphs(glucoseClusters, glucoseDataPointCount);

  return {
    header,
    executiveSummary,
    outputCharts,
    mealMatrix,
    feedingSummary,
    notesTimeline,
    rawEvents: safeRawEvents,
    clusteredVitals: {
      bloodPressure: { hasData: bpGraphs.length > 0, graphs: bpGraphs },
      glucose,
      vitalSignsRecord: buildGroupedVitalSignsRecord(bloodPressureClusters, glucoseClusters),
    },
  };
}

/**
 * Build a chronologically sorted notes timeline from the MonthlyNotesReport.
 * Collects all tagged notes from notesByDate and sorts by recordedAt.
 */
function buildNotesTimeline(notesReport: MonthlyNotesReport): TaggedNote[] {
  const allNotes: TaggedNote[] = [];

  if (notesReport.notesByDate) {
    for (const dateKey of Object.keys(notesReport.notesByDate)) {
      const dayNotes = notesReport.notesByDate[dateKey];
      if (Array.isArray(dayNotes)) {
        allNotes.push(...dayNotes);
      }
    }
  }

  // Sort chronologically by recordedAt
  allNotes.sort((a, b) => a.note.recordedAt.localeCompare(b.note.recordedAt));

  return allNotes;
}
